
/* global PostMessageService */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  VisitorMessage,
  OperatorMessage,
  TypingMessage,
  ErrorMessage,
  SystemMessage,
  FileMessage,
} from './messages';
import CallingSpinner from './utils/CallingSpinner.jsx';
import ScrollButton from './utils/ScrollButton.jsx';
import CustomScrollVisitor from '../../../../visitor/dev/src/libs/common/custom-scroll';
import CompanyHeader from '../../../../visitor/dev/src/components/company-header/CompanyHeader.jsx';
import LinkPreview from '../../components/link-preview/LinkPreview.jsx';

import createFileTransferExtraParams from '../chat-tools-area/utils/createFileTransferExtraParams';
import createFileTransferText from '../../../../visitor/dev/src/libs/additional-params-creator/creators/createFileTransferText';

import { MessageTypes, MessageSubtypes } from './messages/constants';

import './style';
import { transformUnicode10to16 } from '../../../../operator/dev/src/libs/utils/common';

const isVisitor = process.env.APP_TYPE === 'visitor';
const isIEorFF = process.env.BROWSER === 'ff' || process.env.BROWSER === 'ie';
const AUTOSCROLL_BORDER = 300;
const INITIAL_SCROLL_TIMEOUT = 500;

class MessageDisplayArea extends Component {
  constructor(props) {
    super(props);
    const { options } = this.props;
    this.isShow = options.isChatShouldShowUp;
    this.isWhenOpeningScrollToEnd = false;
    this.shouldScrollTopAfterOpen = false;
    this.state = {
      isAutoscrollEnabled: true,
      unreadMessages: 0,
    }
  }

  componentDidMount() {
    const { options: { initialScrollTop, draftUnreadMessages }, onScroll } = this.props;
    this.scrollerRef = isIEorFF ? this.customScrollRef : this.wrapperRef;

    if (!this.isShow && this.scrollerRef.offsetHeight) {
      this.isShow = true;
    }

    if (initialScrollTop !== null) {
      if (this.isShow) {
        setTimeout(() => {
          if (!this.isBlockInitialScroll) {
            this.scrollerRef.scrollTop = initialScrollTop;
            onScroll(initialScrollTop);
            this.handleScroll();
          }
        }, INITIAL_SCROLL_TIMEOUT);
      } else {
        this.shouldScrollTopAfterOpen = true;
      }
    } else {
      this.scrollDown();
    }

    this.setState({ unreadMessages: draftUnreadMessages })

    if (isVisitor && isIEorFF) {
      this.customScrollVisitor = new CustomScrollVisitor(this.customScrollRef, initialScrollTop);
    }
  }

  componentDidUpdate({ messages: prevMessages, options: prevOptions, typings: prevTypings }) {
    const { unreadMessages } = this.state;
    const {
      messages, typings, options: { roomId, customScrollTop, draftUnreadMessages }, saveDraftUnreadMessages,
    } = this.props;
    const isNewMessage = messages.length > prevMessages.length;
    const isNewTyping = typings.length > prevTypings.length;

    if (isNewMessage || isNewTyping) {
      this.checkShouldScroll(isNewTyping ? typings[typings.length - 1] : messages[messages.length - 1]);
    }
    if (!isVisitor && roomId !== prevOptions.roomId) {
      if (customScrollTop !== null) {
        this.scrollerRef.scrollTop = customScrollTop;
        this.handleScroll();
      } else this.scrollDown();
      saveDraftUnreadMessages(prevOptions.roomId, unreadMessages);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ unreadMessages: draftUnreadMessages });
    }
  }

  componentWillUnmount() {
    const { saveDraftUnreadMessages, options } = this.props;
    const { unreadMessages } = this.state;
    if (isVisitor && isIEorFF) {
      this.customScrollVisitor.unmount();
    }
    if (!isVisitor) {
      saveDraftUnreadMessages(options.roomId, unreadMessages);
    }
  }

  setIsCalling = isCalling => {
    this.setState({ isCalling });
  }

  onHide = () => {
    this.reserveScrollTop = this.scrollerRef.scrollTop;
    this.isShow = false;
  }

  onShow = () => {
    const { options: { initialScrollTop } } = this.props;
    this.isShow = true;

    if (this.shouldScrollTopAfterOpen) {
      this.scrollerRef.scrollTop = initialScrollTop;
    } else if (this.isWhenOpeningScrollToEnd) {
      this.scrollDown();
    } else if (this.customScrollVisitor) {
      this.customScrollVisitor.changeScrollPositionByBarPosition();
    }

    this.shouldScrollTopAfterOpen = false;
    this.isWhenOpeningScrollToEnd = false;
  }

  handleRemoveMessage = index => () => {
    const { onRemoveMessage } = this.props;
    onRemoveMessage(index);
  }

  composeMessageProps = (message, index) => {
    const { options, messages, typings, texts } = this.props;
    const { operatorsData, messageViewType, showTime, additionalMessageOptions } = options;
    return {
      ...message,
      ...additionalMessageOptions,
      showTime,
      texts,
      messageViewType,
      onScrollDown: this.scrollDown,
      imgParams: operatorsData && operatorsData[message.nickname],
      prevMessageParams: messages[index - 1],
      nextMessageParams: messages[index + 1] || typings[0],
      key: message.id,
    };
  }

  /* SCROLL */
  checkShouldScroll = lastMessage => {
    const { options: { isShowNewMessagesAlert, nickname } } = this.props;
    const { isAutoscrollEnabled, unreadMessages } = this.state;
    if (!lastMessage || (lastMessage.subtype === MessageSubtypes.PREVIEW && !isAutoscrollEnabled)) return;

    if (isVisitor && isShowNewMessagesAlert && lastMessage.subtype !== MessageSubtypes.PREVIEW && lastMessage.type !== 'typing') {
      PostMessageService.sendEvent({
        name: 'message-received',
        data: transformUnicode10to16(lastMessage.text).trim(),
      });
    } /* TODO: эту логику переселить в визитор/ */

    if (isAutoscrollEnabled || lastMessage.nickname === nickname) {
      this.scrollDown();
      return;
    }

    if (lastMessage.type !== 'typing') {
      this.setState({ unreadMessages: unreadMessages + 1 });
    }
  }

  handleScroll = () => {
    const { onScroll } = this.props;
    const { isAutoscrollEnabled } = this.state;
    const { scrollHeight, clientHeight, scrollTop } = this.scrollerRef;
    const scrollBottom = scrollHeight - clientHeight - scrollTop;

    if (scrollBottom > AUTOSCROLL_BORDER && isAutoscrollEnabled) {
      this.setState({ isAutoscrollEnabled: false });
      return;
    }
    if (scrollBottom <= AUTOSCROLL_BORDER && !isAutoscrollEnabled) {
      this.setState({ isAutoscrollEnabled: true, unreadMessages: 0 });
    }

    if (!isVisitor) {
      onScroll(scrollTop);
    }
  }

  scrollDown = () => {
    const { onScroll } = this.props;
    const { isAutoscrollEnabled, unreadMessages } = this.state;

    if (!this.isShow && !this.scrollerRef.offsetHeight) {
      this.isWhenOpeningScrollToEnd = true;
    } else {
      this.scrollerRef.scrollTop = this.scrollerRef.scrollHeight;
    }
    if (!isAutoscrollEnabled) {
      this.setState({ isAutoscrollEnabled: true });
    }
    if (unreadMessages) {
      this.setState({ unreadMessages: 0 });
    }
    !isVisitor && onScroll(this.scrollerRef.scrollTop);
  }
  /* /SCROLL */

  render() {
    const {
      messages, typings, options, onRepickFile, fileMessageText,
    } = this.props;
    const { isAutoscrollEnabled, unreadMessages } = this.state;
    const { companyHeaderParams } = options;
    const wrapperClassName = `message-display-area custom-scroll ${options.messageViewType || ''}`;
    const companyHeader = process.env.APP_TYPE === 'visitor' ? (<CompanyHeader params={companyHeaderParams} />) : null;

    return (
      <React.Fragment key="key">
        { !isAutoscrollEnabled && <ScrollButton unreadMessages={unreadMessages} onClick={this.scrollDown} /> }

        <div
          className={wrapperClassName}
          ref={ref => { this.wrapperRef = ref; }}
          onScroll={this.handleScroll}
        >

          <div
            className="message-dispay-area__wrapper custom-scroll"
            ref={ref => { this.customScrollRef = ref; }}
          >
            {companyHeader}

            { options.showSpinner && <CallingSpinner />}

            { messages.map((message, index) => {
              const messageProps = this.composeMessageProps(message, index);
              const isVisitorMessage = message.type === MessageTypes.VISITOR;
              switch (message.type) {
                case MessageTypes.SYSTEM:
                  return (
                    <SystemMessage {...messageProps} />
                  );

                case MessageTypes.OPERATOR:
                case MessageTypes.VISITOR:
                  if (message.subtype === MessageSubtypes.FILE_UPLOAD) {
                    const submitExtraParams = createFileTransferExtraParams({
                      ...options.fileTransfer, clientTimestamp: message.clientTimestamp });

                    return (
                      <FileMessage
                        {...messageProps}
                        textProps={isVisitorMessage
                          ? createFileTransferText(fileMessageText) || createFileTransferText()
                          : fileMessageText || createFileTransferText()}
                        onRemove={this.handleRemoveMessage(index)}
                        onRepick={onRepickFile}
                        submitExtraParams={submitExtraParams}
                      />
                    );
                  }

                  if (message.subtype === MessageSubtypes.PREVIEW) {
                    const { title, description, image, domain, url, type, id } = message;
                    return (
                      <LinkPreview
                        title={title}
                        description={description}
                        image={image}
                        domain={domain}
                        url={url}
                        type={type}
                        key={id}
                        {...messageProps}
                      />
                    )
                  }

                  return isVisitorMessage ? <VisitorMessage {...messageProps} /> : <OperatorMessage {...messageProps} />

                default:
                  return (
                    <ErrorMessage {...messageProps} />
                  );
              }
            })}

            { options.showTypings && typings.map(messageParams => (
              <TypingMessage
                nickname={messageParams.nickname}
                imgParams={options.operatorsData[messageParams.nickname]}
                messageViewType={options.messageViewType}
                onScrollDown={this.scrollDown}
                prevMessageParams={messages[messages.length - 1]}
                key={messageParams.id}
              />
            ))}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

if (process.env.NODE_ENV !== 'production') {
  MessageDisplayArea.propTypes = {
    messages: PropTypes.array.isRequired,
    typings: PropTypes.array.isRequired,
    texts: PropTypes.object.isRequired,
    options: PropTypes.shape({
      showTime: PropTypes.bool,
      showSpinner: PropTypes.bool,
      showTypings: PropTypes.bool,
      fileTransfer: PropTypes.object,
      isChatShouldShowUp: PropTypes.bool,
      initialScrollTop: PropTypes.number,
      isShowNewMessagesAlert: PropTypes.bool,
      scrollTop: PropTypes.number,
      messageViewType: PropTypes.string,
      operatorsData: PropTypes.object,
      companyHeaderParams: PropTypes.object,
      nickname: PropTypes.string,
      roomId: PropTypes.string,
      customScrollTop: PropTypes.number,
      draftUnreadMessages: PropTypes.number,
      additionalMessageOptions: PropTypes.object,
    }).isRequired,
    onRemoveMessage: PropTypes.func,
    onRepickFile: PropTypes.func,
    onScroll: PropTypes.func,
    saveDraftUnreadMessages: PropTypes.func,
    fileMessageText: PropTypes.object,
  };
}

export default MessageDisplayArea;
